<template>
  <el-container class="chat-page" type="flex" style="flex-wrap:wrap">
    <el-aside width="100%" class="chat-list-box">
      <div class="chat-list-header">
        <el-input class="search-text" size="small" placeholder="搜索" v-model="searchText">
          <i class="el-icon-search el-input__icon" slot="prefix"> </i>
        </el-input>
        <el-button
          type="text"
          icon="el-icon-plus"
          size="small"
          class="new-terminal-chat-btn"
          @click="showTerminalSelector = true"
          title="新建终端会话"
        >
          终端
        </el-button>
      </div>
      <div class="chat-list-loading" v-if="loading" v-loading="true" element-loading-text="消息接收中..."
        element-loading-spinner="el-icon-loading" element-loading-background="#F9F9F9" element-loading-size="24">
      </div>
      <el-scrollbar class="chat-list-items" v-else>
        <div v-for="(chat, index) in filteredChats" :key="index">
          <chat-item v-show="!chat.delete && chat.showName && chat.showName.includes(searchText)" :chat="chat" :index="index"
            @click.native="onActiveItem(index)" @delete="onDelItem(index)" @top="onTop(index)"
            :active="chat === chatStore.activeChat" :class="{
              'terminal-inbox': chat.isTerminalInbox,
              'normal-chat': !chat.isTerminalInbox
            }"></chat-item>
        </div>
      </el-scrollbar>
    </el-aside>
 
    <!-- 终端选择器 -->
    <terminal-selector
      :visible.sync="showTerminalSelector"
      @terminal-selected="handleTerminalSelected"
    />
  </el-container>
</template>

<script>
import ChatItem from "../components/chat/ChatItem.vue";
import TerminalSelector from "../components/chat/TerminalSelectorPlugin.vue";

export default {
  name: "chat",
  components: {
    ChatItem,
    TerminalSelector
  },
  data() {
    return {
      searchText: "",
      messageContent: "",
      group: {},
      groupMembers: [],
      // 终端间通信相关
      showTerminalSelector: false,
      currentTerminal: null,  // 当前终端类型
      terminalChats: []       // 终端会话列表
    }
  },
  methods: {
    // 获取当前终端类型
    getCurrentTerminal() {
      // InboxPluginSession 是插件端专用界面
      // 固定返回 4 表示插件端
      return 4; // 插件端
    },
    
    // 初始化其他终端会话
    initOtherTerminalChats() {
      const userInfo = this.$store.state.userStore.userInfo;
      if (!userInfo || !userInfo.id) {
        console.log('[InboxPluginSession] 用户信息不存在，无法初始化终端会话');
        return;
      }
      
      const currentTerminal = this.getCurrentTerminal();
      this.currentTerminal = currentTerminal;
      
      // 定义所有终端类型
      const terminals = [
        { type: 0, name: 'Web', icon: '🌐' },
        { type: 1, name: 'App', icon: '📱' },
        { type: 2, name: 'VSCode', icon: '💻' },
        { type: 3, name: 'PC', icon: '🖥️' },
        { type: 4, name: '插件', icon: '🔌' }
      ];
      
      // 清理已存在的终端会话
      this.cleanupTerminalChatsFromStore();
      
      // 预先添加其他终端会话到 chatStore.chats 中（不包括当前终端）
      terminals.forEach(terminal => {
        // 跳过当前终端（插件端）
        if (terminal.type === currentTerminal) {
          return;
        }
        
        const targetId = `${userInfo.id}_${terminal.type}`;
        
        // 检查是否已存在该终端会话
        const existingChat = this.chatStore.chats.find(chat => 
          chat.isTerminalInbox && chat.targetId === targetId
        );
        
        if (!existingChat) {
          // 创建终端会话
          const chatInfo = {
            type: 'PRIVATE',
            targetId: targetId,
            receivingTerminal: terminal.type,
            senderTerminal: 4, // 插件端发送
            showName: `${terminal.name}端`,
            headImage: terminal.icon,
            isTerminalInbox: true,
            lastContent: '',
            lastSendTime: Date.now(),
            unreadCount: 0,
            delete: false,
            messages: [],
            stored: false,
            lastTimeTip: Date.now()
          };
          
          // 使用 openChat 方法添加会话
          this.$store.commit('openChat', chatInfo);
          
          // 立即检查是否需要发送系统消息
          this.$nextTick(async () => {
            const chat = this.chatStore.chats.find(c => 
              c.targetId === targetId && c.type === 'PRIVATE'
            );
            
            if (chat) {
              // 清理任何空消息
              if (chat.messages && chat.messages.length > 0) {
                chat.messages = chat.messages.filter((msg, index) => {
                  if (!msg || msg.type === undefined || msg.type === null) {
                    return false;
                  }
                  if (msg.type === 0 && (!msg.content || msg.content.trim() === '')) {
                    return false;
                  }
                  if (msg.type === 8 && (!msg.content || msg.content.trim() === '')) {
                    return false;
                  }
                  return true;
                });
                
                if (chat.messages.length === 0) {
                  chat.stored = false;
                }
              }
              
              // 如果没有有效消息，发送系统消息
              if (!chat.messages || chat.messages.length === 0) {
                await this.sendSystemMessage(chatInfo, terminal.type);
              }
            }
          });
        }
      });
      
      // 清理所有会话中的空消息
      this.$store.commit('cleanEmptyMessages');
      
      // 立即将终端会话置顶
      this.moveTerminalChatsToTop();
      
      console.log('[InboxPluginSession] 终端会话初始化完成');
    },
    
    // 清理 chatStore 中的终端会话
    cleanupTerminalChatsFromStore() {
      // 移除 chatStore.chats 中所有的终端会话
      const terminalChatIndices = [];
      this.chatStore.chats.forEach((chat, index) => {
        if (chat.isTerminalInbox) {
          terminalChatIndices.push(index);
        }
      });
      
      // 从后往前删除，避免索引问题
      for (let i = terminalChatIndices.length - 1; i >= 0; i--) {
        this.chatStore.chats.splice(terminalChatIndices[i], 1);
      }
    },
    
    // 将终端会话移到顶部
    moveTerminalChatsToTop() {
      // 使用mutation来置顶终端会话
      this.$store.commit('sortTerminalChatsToTop');
    },
    
    // 发送系统消息
    async sendSystemMessage(chatInfo, terminal) {
      // 获取终端类型对应的消息内容
      let terminalKey = null;
      const terminalKeys = {
        0: 'web',
        1: 'app',
        2: 'vscode',
        3: 'pc',
        4: 'plugin'
      };
      
      terminalKey = terminalKeys[terminal];
      let content = `欢迎使用傻蛋IM ${this.getTerminalName(terminal)}端`; // 默认消息
      
      // 尝试从远程获取消息内容
      if (terminalKey) {
        try {
          const response = await fetch('https://aiim.service.thinkgs.cn/text.json');
          if (response.ok) {
            const data = await response.json();
            if (data && data[terminalKey]) {
              content = data[terminalKey];
              console.log(`[InboxPluginSession] 从远程获取${terminalKey}端系统消息:`, content);
            }
          }
        } catch (error) {
          console.error('[InboxPluginSession] 获取远程系统消息失败:', error);
        }
      }
      
      // 检查内容是否为空
      if (!content || content.trim() === '') {
        console.warn('[InboxPluginSession] 系统消息内容为空，跳过发送');
        return null;
      }
      
      // 先确保会话存在
      let chat = this.chatStore.chats.find(c => 
        c.targetId === chatInfo.targetId && c.type === chatInfo.type
      );
      
      if (!chat) {
        console.warn('[InboxPluginSession] 发送系统消息时找不到会话:', chatInfo.targetId);
        return null;
      }
      
      const systemMessage = {
        id: `sys_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        tmpId: `tmp_sys_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: 21, // MESSAGE_TYPE.TIP_TEXT = 21
        content: content.trim(),
        sendTime: Date.now(),
        selfSend: false,
        status: 1,
        sendNickName: '系统',
        sendId: -1,
        recvId: this.$store.state.userStore.userInfo?.id || 0
      };
      
      console.log('[InboxPluginSession] 发送系统消息:', systemMessage);
      
      // 将系统消息添加到会话中
      this.$store.commit('insertMessage', [systemMessage, chatInfo]);
      
      // 更新会话的最后消息
      chat.lastContent = content;
      chat.lastSendTime = systemMessage.sendTime;
      
      return systemMessage;
    },
    
    onActiveItem(index) {
      this.$store.commit("activeChat", index);
      
      // 获取当前激活的聊天对象
      const activeChat = this.chatStore.chats[index];
      if (activeChat) {
        // 触发父组件事件，切换到聊天视图
        this.$emit('chatItemClick', {
          type: activeChat.type,
          targetId: activeChat.targetId,
          showName: activeChat.showName,
          headImage: activeChat.headImage,
        });
      }
      
      // VSCode环境中，同时打开编辑器中的聊天窗口
      // if (window.vscodeServices && window.vscodeServices.openChatEditor) {
      //   if (activeChat) {
      //     // 构造用户信息对象，适配openChatEditor所需的格式
      //     const chatUser = {
      //       id: activeChat.targetId || activeChat.id,
      //       nickName: activeChat.showName || activeChat.name,
      //       name: activeChat.showName || activeChat.name,
      //       headImage: activeChat.headImage || activeChat.avatar,
      //       headImageThumb: activeChat.headImageThumb || activeChat.avatar,
      //       avatar: activeChat.headImage || activeChat.avatar
      //     };
          
      //     console.log('[InboxSession] Opening chat editor for user:', chatUser.nickName);
          
      //     // 调用VSCode服务打开聊天编辑器
      //     try {
      //       window.vscodeServices.openChatEditor(chatUser);
      //       console.log('[InboxSession] Chat editor opened successfully');
      //     } catch (error) {
      //       console.error('[InboxSession] Failed to open chat editor:', error);
      //     }
      //   }
      // }
    },
    // 处理终端选择
    async handleTerminalSelected(receivingTerminal) {
      console.log('[Chat] 选择的终端:', receivingTerminal);
      
      try {
        // 1. 获取当前用户信息
        const userInfo = this.$store.state.userStore.userInfo;
        if (!userInfo || !userInfo.id) {
          this.$message.error('用户未登录');
          return;
        }

        const targetId = `${userInfo.id}_${receivingTerminal}`;
        
        // 2. 检查会话是否已存在，避免重复创建
        const existingChatIndex = this.chatStore.chats.findIndex(chat => 
          chat.targetId === targetId && chat.isTerminalInbox
        );
        
        if (existingChatIndex !== -1) {
          // 会话已存在，直接激活
          console.log('[Chat] 终端会话已存在，直接激活');
          this.onActiveItem(existingChatIndex);
          this.$message.success(`已切换到${userInfo.nickName}(${this.getTerminalName(receivingTerminal)}端)会话`);
          return;
        }
        let heademoji= '👤'
        if(receivingTerminal === 0){
          heademoji='🌐'
        }
        if(receivingTerminal === 1){
          heademoji='📱'
        }
        if(receivingTerminal === 2){
          heademoji='💻'
        }
        if(receivingTerminal === 3){
          heademoji='🖥️'
        }
        if(receivingTerminal === 4){
          heademoji='🔌'
        }
        // 3. 创建新的终端收件箱会话
        const chatInfo = {
          type: 'PRIVATE',
          targetId: targetId,
          receivingTerminal: receivingTerminal,
          senderTerminal: 4,
          showName: `${userInfo.nickName}(${this.getTerminalName(receivingTerminal)}端)`,
          headImage: userInfo.headImage || userInfo.avatar || heademoji,
          isTerminalInbox: true
        };

        // 4. 打开会话（只在不存在时创建）
        this.$store.commit('openChat', chatInfo);

        // 5. 查找并激活会话
        const chatIndex = this.$store.getters.findChatIdx(chatInfo);
        if (chatIndex !== undefined) {
          this.onActiveItem(chatIndex);
        }

        this.$message.success(`已创建${userInfo.nickName}(${this.getTerminalName(receivingTerminal)}端)会话`);
      } catch (error) {
        console.error('[Chat] 创建终端会话失败:', error);
        this.$message.error('创建终端会话失败');
      }
    },

    // 获取终端名称
    getTerminalName(terminal) {
      const terminalNames = {
        0: 'Web',
        1: 'App', 
        2: 'VSCode',
        3: 'PC',
        4: '插件',
        5: 'MCP',
        6: '小龙虾'
      };
      return terminalNames[terminal] || '未知';
    },
    // 获取终端图标
    getTerminalIcon(terminal) {
      const terminalIcons = {
        0: '🌐',  // Web
        1: '📱',  // App
        2: '💻',  // VSCode
        3: '🖥️',   // PC
        4: '🔌',   //插件
        5: '🤖',   // MCP
        6: '🦞'   // 小龙虾
      };
      return terminalIcons[terminal] || '❓';
    },
    onDelItem(index) {
      this.$store.commit("removeChat", index);
    },
    onTop(chatIdx) {
      this.$store.commit("moveTop", chatIdx);
    },
  },
  computed: {
    chatStore() {
      return this.$store.state.chatStore;
    },
    loading() {
      return this.chatStore.loadingGroupMsg || this.chatStore.loadingPrivateMsg
    },
    // 过滤后的会话列表（排除插件端自己的会话）
    filteredChats() {
      const currentTerminal = this.getCurrentTerminal(); // 插件端 = 4
      
      // 过滤掉插件端自己的终端会话
      return this.chatStore.chats.filter(chat => {
        // 如果是终端会话，检查是否是插件端
        if (chat.isTerminalInbox) {
          // 过滤掉插件端的会话
          if (chat.receivingTerminal === currentTerminal) {
            console.log(`[InboxPluginSession] 过滤掉插件端会话: ${chat.showName}`);
            return false;
          }
        }
        return true;
      });
    }
  },
  
  mounted() {
    console.log('[InboxPluginSession] 组件挂载，开始初始化终端会话...');
    
    // 初始化其他终端会话
    this.initOtherTerminalChats();
    
    // 确保终端会话置顶
    this.$nextTick(() => {
      this.moveTerminalChatsToTop();
    });
    
    // 监听用户信息变化
    this.$watch(
      () => this.$store.state.userStore.userInfo,
      (newVal) => {
        if (newVal && newVal.id) {
          this.initOtherTerminalChats();
          // 用户信息变化后也要确保置顶
          this.$nextTick(() => {
            this.moveTerminalChatsToTop();
          });
        }
      },
      { deep: true }
    );
    
    // 监听 chatStore 中的会话变化
    this.$watch(
      () => this.chatStore.chats,
      () => {
        // 检查是否需要重新置顶终端会话
        if (this.chatStore.chats && this.chatStore.chats.length > 0) {
          const firstChat = this.chatStore.chats[0];
          if (!firstChat.isTerminalInbox) {
            console.log('[InboxPluginSession] 检测到会话列表变化，重新置顶终端会话');
            this.$nextTick(() => {
              this.moveTerminalChatsToTop();
            });
          }
        }
      },
      { deep: true }
    );
  }
}
</script>

<style lang="scss">
.chat-page {
  height: 100% !important;
  
  // 重写Element UI的el-container样式
  
  
  .chat-list-box {
    height: 100% !important;
    
    // 重写Element UI的el-aside样式
    &.el-aside {
      height: 100% !important;
    }
    
    display: flex;
    flex-direction: column;
    background: var(--im-background);

    .chat-list-header {
      height: 50px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      padding: 0 2px 0 8px;

      .search-text {
        flex: 1;
        margin-right: 8px;
      }

      .new-terminal-chat-btn {
        padding: 4px 8px;
        font-size: 12px;
        border-radius: 12px;
        background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
        color: white;
        border: none;
        transition: all 0.2s ease;
        flex: 0 0 auto;
        min-width: 50px;

        &:hover {
          background: linear-gradient(135deg, #5f4fcf 0%, #9088e8 100%);
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(108, 92, 231, 0.3);
        }

        &:active {
          transform: translateY(0);
        }

        &:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba(108, 92, 231, 0.2);
        }
      }
    }

    .chat-list-loading {
      height: 50px;
      flex-shrink: 0;
      background-color: #eee;

      .el-icon-loading {
        font-size: 24px;
        color: var(--im-text-color-light);
      }

      .el-loading-text {
        color: var(--im-text-color-light);
      }

      .chat-loading-box {
        height: 100%;
      }
    }

    .chat-list-items {
      flex: 1;
      height: 0; // 强制flex子元素高度计算
    }
  }
}
</style>
